import * as React from "react";

import { cn } from "../lib/cn";
import { clamp } from "../lib/math";
import {
  getSeekBarWhiteTrackStyle,
  SEEK_BAR_FOCUS_RING_CLASSES,
} from "../lib/shared-styles";

export type SeekBarSegment = {
  id: string;
  startTimeSeconds: number;
  endTimeSeconds: number;
  isFallback?: boolean;
};

export type SegmentedSeekBarTooltipContext = {
  hoverTimeSeconds: number;
  anchorLeftPx: number;
  isInteracting: boolean;
};

export type SegmentedSeekBarProps = {
  /** committed/live playback time in seconds */
  value: number;
  /** total seekable duration in seconds */
  max: number;
  segments: Array<SeekBarSegment>;
  color?: "black" | "white";
  disabled?: boolean;
  keyboardStepSeconds?: number;
  ariaLabel?: string;
  /** announced as aria-valuetext, e.g. "12:30 — Chapter title" */
  ariaValueText?: string;
  onSeek?: (timeSeconds: number) => void;
  onSeekStart?: () => void;
  onSeekEnd?: (timeSeconds: number) => void;
  onStepChapter?: (direction: "previous" | "next") => void;
  renderTooltip?: (ctx: SegmentedSeekBarTooltipContext) => React.ReactNode;
  showTooltip?: boolean;
  className?: string;
  "data-testid"?: string;
};

const GAP_EPSILON_SECONDS = 0.05;

type RenderSegment = {
  id: string;
  startTimeSeconds: number;
  endTimeSeconds: number;
  durationSeconds: number;
  startPercent: number;
  widthPercent: number;
  isFallback: boolean;
};

export function buildRenderSegments(
  segments: Array<SeekBarSegment>,
  duration: number,
): Array<RenderSegment> {
  if (duration <= 0) {
    return [];
  }

  const sorted = [...segments].sort(
    (a, b) => a.startTimeSeconds - b.startTimeSeconds,
  );
  const result: Array<RenderSegment> = [];
  let cursor = 0;

  sorted.forEach((segment, index) => {
    if (segment.startTimeSeconds > cursor + GAP_EPSILON_SECONDS) {
      const gapStart = cursor;
      const gapEnd = segment.startTimeSeconds;
      const gapDuration = gapEnd - gapStart;
      result.push({
        id: `segmented-seek-bar-fallback-${index}-leading`,
        startTimeSeconds: gapStart,
        endTimeSeconds: gapEnd,
        durationSeconds: gapDuration,
        startPercent: (gapStart / duration) * 100,
        widthPercent: (gapDuration / duration) * 100,
        isFallback: true,
      });
    }

    const segmentDuration = segment.endTimeSeconds - segment.startTimeSeconds;
    result.push({
      id: segment.id,
      startTimeSeconds: segment.startTimeSeconds,
      endTimeSeconds: segment.endTimeSeconds,
      durationSeconds: segmentDuration,
      startPercent: (segment.startTimeSeconds / duration) * 100,
      widthPercent: (segmentDuration / duration) * 100,
      isFallback: segment.isFallback ?? false,
    });

    cursor = Math.max(cursor, segment.endTimeSeconds);
  });

  if (cursor < duration - GAP_EPSILON_SECONDS) {
    const gapStart = cursor;
    const gapDuration = duration - gapStart;
    result.push({
      id: "segmented-seek-bar-fallback-trailing",
      startTimeSeconds: gapStart,
      endTimeSeconds: duration,
      durationSeconds: gapDuration,
      startPercent: (gapStart / duration) * 100,
      widthPercent: (gapDuration / duration) * 100,
      isFallback: true,
    });
  }

  return result;
}

type InteractionState =
  | { status: "idle" }
  | { status: "hovering"; hoverTimeSeconds: number; anchorLeftPx: number }
  | {
      status: "dragging";
      hoverTimeSeconds: number;
      anchorLeftPx: number;
      draftTimeSeconds: number;
      dragDurationSeconds: number;
    };

type InteractionAction =
  | { type: "HOVER_MOVE"; hoverTimeSeconds: number; anchorLeftPx: number }
  | { type: "HOVER_LEAVE" }
  | {
      type: "DRAG_START";
      hoverTimeSeconds: number;
      anchorLeftPx: number;
      dragDurationSeconds: number;
    }
  | { type: "DRAG_MOVE"; hoverTimeSeconds: number; anchorLeftPx: number }
  | { type: "DRAG_END" }
  | { type: "DRAG_CANCEL" };

const IDLE_STATE: InteractionState = { status: "idle" };

function interactionReducer(
  state: InteractionState,
  action: InteractionAction,
): InteractionState {
  switch (action.type) {
    case "HOVER_MOVE":
      if (state.status === "dragging") {
        return state;
      }

      return {
        status: "hovering",
        hoverTimeSeconds: action.hoverTimeSeconds,
        anchorLeftPx: action.anchorLeftPx,
      };
    case "HOVER_LEAVE":
      return state.status === "dragging" ? state : IDLE_STATE;
    case "DRAG_START":
      return {
        status: "dragging",
        hoverTimeSeconds: action.hoverTimeSeconds,
        anchorLeftPx: action.anchorLeftPx,
        draftTimeSeconds: action.hoverTimeSeconds,
        dragDurationSeconds: action.dragDurationSeconds,
      };
    case "DRAG_MOVE":
      if (state.status !== "dragging") {
        return state;
      }

      return {
        ...state,
        hoverTimeSeconds: action.hoverTimeSeconds,
        anchorLeftPx: action.anchorLeftPx,
        draftTimeSeconds: action.hoverTimeSeconds,
      };
    case "DRAG_END":
    case "DRAG_CANCEL":
      return IDLE_STATE;
    default:
      return state;
  }
}

export const SegmentedSeekBar = React.forwardRef<
  HTMLDivElement,
  SegmentedSeekBarProps
>(
  (
    {
      value,
      max,
      segments,
      color = "white",
      disabled = false,
      keyboardStepSeconds = 5,
      ariaLabel = "Seek audio",
      ariaValueText,
      onSeek,
      onSeekStart,
      onSeekEnd,
      onStepChapter,
      renderTooltip,
      showTooltip = true,
      className,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const trackRef = React.useRef<HTMLDivElement | null>(null);
    const [interaction, dispatch] = React.useReducer(
      interactionReducer,
      IDLE_STATE,
    );

    const duration = Math.max(Number.isFinite(max) ? max : 0, 0);
    const liveTime = clamp(value, 0, duration);
    // While dragging, bind the visual scrubber to the local draft so transient engine
    // state (track switches reporting position/duration of 0) cannot snap the thumb back.
    const currentTime =
      interaction.status === "dragging"
        ? interaction.draftTimeSeconds
        : liveTime;
    const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
    const isInteracting = interaction.status !== "idle";
    const isDragging = interaction.status === "dragging";
    const renderSegments = buildRenderSegments(segments, duration);
    const trackColorClass = color === "white" ? "" : "bg-black/[0.14]";
    const fillColorClass = color === "white" ? "bg-white" : "bg-[#111111]";
    const whiteTrackStyle = getSeekBarWhiteTrackStyle(color);
    const tooltipContext: SegmentedSeekBarTooltipContext =
      interaction.status === "idle"
        ? { hoverTimeSeconds: 0, anchorLeftPx: 0, isInteracting: false }
        : {
            hoverTimeSeconds: interaction.hoverTimeSeconds,
            anchorLeftPx: interaction.anchorLeftPx,
            isInteracting: true,
          };

    function assignRef(node: HTMLDivElement | null) {
      trackRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    }

    function readPosition(clientX: number, durationOverride?: number) {
      const element = trackRef.current;
      if (!element) {
        return { anchorLeftPx: 0, timeSeconds: 0 };
      }

      const rect = element.getBoundingClientRect();
      const anchorLeftPx = clamp(clientX - rect.left, 0, rect.width);
      const ratio = rect.width > 0 ? anchorLeftPx / rect.width : 0;
      const effectiveDuration = durationOverride ?? duration;

      return {
        anchorLeftPx,
        timeSeconds: ratio * effectiveDuration,
      };
    }

    function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
      if (disabled) {
        return;
      }

      // On touch, prevent the browser from claiming the gesture for scroll / pull-to-refresh
      // which would otherwise fire pointercancel a few pixels into the drag.
      if (event.pointerType !== "mouse") {
        event.preventDefault();
      }

      if ("setPointerCapture" in event.currentTarget) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }

      // Snapshot the duration at drag-start so subsequent moves cannot read a transient 0.
      const { anchorLeftPx, timeSeconds } = readPosition(
        event.clientX,
        duration,
      );
      dispatch({
        type: "DRAG_START",
        hoverTimeSeconds: timeSeconds,
        anchorLeftPx,
        dragDurationSeconds: duration,
      });
      onSeekStart?.();
      onSeek?.(timeSeconds);
    }

    function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
      if (disabled || interaction.status === "idle") {
        return;
      }

      const effectiveDuration =
        interaction.status === "dragging"
          ? interaction.dragDurationSeconds
          : duration;
      const { anchorLeftPx, timeSeconds } = readPosition(
        event.clientX,
        effectiveDuration,
      );

      if (interaction.status === "dragging") {
        dispatch({
          type: "DRAG_MOVE",
          hoverTimeSeconds: timeSeconds,
          anchorLeftPx,
        });
        onSeek?.(timeSeconds);
      } else {
        dispatch({
          type: "HOVER_MOVE",
          hoverTimeSeconds: timeSeconds,
          anchorLeftPx,
        });
      }
    }

    function finishDrag(
      event: React.PointerEvent<HTMLDivElement>,
      commit: boolean,
    ) {
      if (interaction.status !== "dragging") {
        return;
      }

      // pointercancel can fire with stale / zeroed coordinates on touch, so only read a
      // final time when we are actually committing the seek.
      const finalTime = commit
        ? readPosition(event.clientX, interaction.dragDurationSeconds)
            .timeSeconds
        : null;

      dispatch({ type: commit ? "DRAG_END" : "DRAG_CANCEL" });

      if (commit && finalTime !== null && !disabled) {
        onSeekEnd?.(finalTime);
      }

      if (
        "hasPointerCapture" in event.currentTarget &&
        event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }

    function handlePointerEnter(event: React.PointerEvent<HTMLDivElement>) {
      if (disabled) {
        return;
      }

      const { anchorLeftPx, timeSeconds } = readPosition(event.clientX);
      dispatch({
        type: "HOVER_MOVE",
        hoverTimeSeconds: timeSeconds,
        anchorLeftPx,
      });
    }

    function handlePointerLeave() {
      dispatch({ type: "HOVER_LEAVE" });
    }

    function commitKeyboardSeek(nextTime: number) {
      const bounded = clamp(nextTime, 0, duration);
      onSeek?.(bounded);
      onSeekEnd?.(bounded);
    }

    function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
      if (disabled) {
        return;
      }

      switch (event.key) {
        case "ArrowRight":
          event.preventDefault();
          commitKeyboardSeek(liveTime + keyboardStepSeconds);
          break;
        case "ArrowLeft":
          event.preventDefault();
          commitKeyboardSeek(liveTime - keyboardStepSeconds);
          break;
        case "Home":
          event.preventDefault();
          commitKeyboardSeek(0);
          break;
        case "End":
          event.preventDefault();
          commitKeyboardSeek(duration);
          break;
        case "ArrowUp":
          event.preventDefault();
          onStepChapter?.("previous");
          break;
        case "ArrowDown":
          event.preventDefault();
          onStepChapter?.("next");
          break;
        default:
          break;
      }
    }

    return (
      <>
        <div
          ref={assignRef}
          role="slider"
          aria-label={ariaLabel}
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={currentTime}
          aria-valuetext={ariaValueText}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          className={cn(
            "relative h-2 cursor-pointer touch-none select-none rounded-full",
            SEEK_BAR_FOCUS_RING_CLASSES,
            disabled && "cursor-not-allowed opacity-60",
            className,
          )}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={(event) => finishDrag(event, true)}
          onPointerCancel={(event) => finishDrag(event, false)}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onKeyDown={handleKeyDown}
          data-testid={dataTestId}
        >
          {renderSegments.map((segment) => {
            const segmentDuration = Math.max(segment.durationSeconds, 0);
            const segmentProgress =
              segmentDuration > 0
                ? clamp(
                    ((currentTime - segment.startTimeSeconds) /
                      segmentDuration) *
                      100,
                    0,
                    100,
                  )
                : 0;
            const isFullyPlayed = currentTime >= segment.endTimeSeconds;
            const isCurrentSegment =
              currentTime >= segment.startTimeSeconds &&
              currentTime < segment.endTimeSeconds;

            return (
              <div
                key={segment.id}
                data-fallback={segment.isFallback ? "true" : undefined}
                data-testid="segmented-seek-bar-segment"
                className={cn(
                  "absolute top-1/2 h-[4px] -translate-y-1/2 overflow-hidden rounded-full",
                  trackColorClass,
                )}
                style={{
                  // Position segments in the same coordinate space as the thumb so they cannot
                  // drift apart. The 1.5px inset on each side preserves the visual 3px gap
                  // without consuming container width like flex `gap` does.
                  left: `calc(${segment.startPercent}% + 1.5px)`,
                  width: `calc(${segment.widthPercent}% - 3px)`,
                  ...whiteTrackStyle,
                }}
              >
                <div
                  className={cn(
                    "absolute inset-y-0 left-0 rounded-full",
                    fillColorClass,
                  )}
                  style={{
                    width: isFullyPlayed
                      ? "100%"
                      : isCurrentSegment
                        ? `${segmentProgress}%`
                        : "0%",
                  }}
                />
              </div>
            );
          })}

          <div
            className={cn(
              "pointer-events-none absolute top-1/2 z-10 h-3 w-3 -translate-y-1/2 rounded-full shadow-md transition-transform",
              // Grow the 12px thumb to 16px while pressed (16 / 12).
              isDragging && "scale-[1.3333]",
              fillColorClass,
            )}
            style={{
              left: `calc(${progressPercent}% - 6px)`,
            }}
            data-testid="segmented-seek-bar-thumb"
          />
        </div>
        {showTooltip && renderTooltip && isInteracting
          ? renderTooltip(tooltipContext)
          : null}
      </>
    );
  },
);
SegmentedSeekBar.displayName = "SegmentedSeekBar";
